import React from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  AnalyticsGraphMetricsEnum,
  AnalyticsHeatMapMetricsEnum,
  AnalyticsMetricsKeys,
  AnalyticsMultiGraphMetricsEnum,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../../models/api/analyticsMetricsModel';
import {
  AnalyticsFeatureStatusesEnum,
  AnalyticsReportConfig,
  analyticsTicketingMetrics,
  ConversationResolutionStrategiesEnum,
  MetricCategoriesEnum,
  ReportElementIdsEnum,
  ReportFilterOptionsEnum,
  ReportMetricConfig,
} from './reportTypes';

const defaultMetricsWithTicketing: Array<
  AnalyticsScalarMetricsEnum | AnalyticsGraphMetricsEnum | AnalyticsTableMetricsEnum
> = [
  AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
  AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME,
  AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
  // Workload over time.
  AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH,
  // Efficiency.
  AnalyticsTableMetricsEnum.TICKET_AVG_RESOLUTION_TIME_HISTO,
];

const defaultMetrics = [
  // Key metrics.
  AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
  AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME, // Needed for Efficiency too.
  AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
  // Workload over time.
  AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH,
  // Efficiency.
  AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
];

// Mapping between stage metrics and ticket metrics to help during the transition with metrics saved in views. Remove when views have been cleaned up.
export const stageToTicketMetrics: Partial<Record<AnalyticsMetricsKeys, AnalyticsMetricsKeys>> = {
  // Scalar:
  [AnalyticsScalarMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS]:
    AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS,
  [AnalyticsScalarMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS]:
    AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
  [AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME]:
    AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME,
  [AnalyticsScalarMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION]:
    AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION,
  [AnalyticsScalarMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY]:
    AnalyticsScalarMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY,
  // Charts:
  [AnalyticsGraphMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS_GRAPH]:
    AnalyticsGraphMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS_GRAPH,
  [AnalyticsGraphMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH]:
    AnalyticsGraphMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH,
  [AnalyticsGraphMetricsEnum.STAGE_AVG_RESOLUTION_TIME_GRAPH]:
    AnalyticsGraphMetricsEnum.TICKET_AVG_RESOLUTION_TIME_GRAPH,
  [AnalyticsGraphMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH]:
    AnalyticsGraphMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH,
  [AnalyticsGraphMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_GRAPH]:
    AnalyticsGraphMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_GRAPH,
  // Histograms:
  [AnalyticsTableMetricsEnum.STAGE_AVG_RESOLUTION_TIME_HISTO]:
    AnalyticsTableMetricsEnum.TICKET_AVG_RESOLUTION_TIME_HISTO,
  [AnalyticsTableMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_HISTO]:
    AnalyticsTableMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_HISTO,
  // GroupBy Metrics:
  [AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_INBOX_GRAPH]:
    AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_INBOX_WITH_TICKETING_GRAPH,
  [AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_TAG_GRAPH]:
    AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_TAG_WITH_TICKETING_GRAPH,
};

/**
 * Based on whether conversation stages are activated or not, dynamically set the default metrics.
 */
function setIsMetricDisplayedByDefault(
  item: Omit<
    ReportMetricConfig<AnalyticsScalarMetricsEnum | AnalyticsTableMetricsEnum | AnalyticsGraphMetricsEnum>,
    'isDisplayedByDefault'
  >,
  isTicketingStrategy: boolean,
): ReportMetricConfig<AnalyticsScalarMetricsEnum | AnalyticsTableMetricsEnum | AnalyticsGraphMetricsEnum> {
  if (isTicketingStrategy) {
    return {
      ...item,
      isDisabled: false,
      isDisplayedByDefault: defaultMetricsWithTicketing.includes(item.id),
    };
  }

  return {
    ...item,
    isDisabled: analyticsTicketingMetrics.includes(item.id),
    isDisplayedByDefault: defaultMetrics.includes(item.id),
  };
}

export function getWorkloadReportConfig(
  conversationResolutionStrategy: ConversationResolutionStrategiesEnum,
): AnalyticsReportConfig {
  const isTicketingStrategy =
    conversationResolutionStrategy === ConversationResolutionStrategiesEnum.TICKETING;
  return {
    title: <Trans id="analytics-workload-report-title">Workload</Trans>,
    reportElements: [
      {
        /* Key Metrics. */
        id: ReportElementIdsEnum.WORKLOAD_KEY_METRICS,
        metrics: [
          {
            id: AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.MESSAGES_SENT,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
        ].map((item) => setIsMetricDisplayedByDefault(item, isTicketingStrategy)),
      },
      {
        /* Workload over time. */
        id: ReportElementIdsEnum.WORKLOAD_OVER_TIME,
        metrics: [
          {
            id: AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsGraphMetricsEnum.NUM_ACTIVE_TEAMMATES_GRAPH,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsGraphMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS_GRAPH,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsGraphMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsGraphMetricsEnum.TICKET_AVG_RESOLUTION_TIME_GRAPH,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsGraphMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsGraphMetricsEnum.RESPONSE_GRAPH,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
        ].map((item) => setIsMetricDisplayedByDefault(item, isTicketingStrategy)),
      },
      {
        /* Busiest times heatmap. */
        id: ReportElementIdsEnum.WORKLOAD_BUSIEST_TIMES,
        metrics: [
          {
            id: AnalyticsHeatMapMetricsEnum.INBOX_WITH_TICKETING,
            isDisplayedByDefault: true,
          },
        ],
        columns: [
          {
            id: AnalyticsScalarMetricsEnum.NUM_NEW_SEGMENTS,
            isDisplayedByDefault: true,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.MESSAGES_SENT,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES,
            isDisplayedByDefault: false,
            category: MetricCategoriesEnum.WORKFORCE,
          },
        ].map((item) => setIsMetricDisplayedByDefault(item, isTicketingStrategy)),
      },
      {
        /* Efficiency donut */
        id: ReportElementIdsEnum.WORKLOAD_EFFICIENCY,
        metrics: [
          // Scalar metrics are also included so that they are fetched for the donut at the same time as the histogram. [
          {
            id: AnalyticsTableMetricsEnum.TICKET_AVG_RESOLUTION_TIME_HISTO,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME,
            category: MetricCategoriesEnum.EFFICIENCY,
          },

          {
            id: AnalyticsTableMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_HISTO,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
            category: MetricCategoriesEnum.EFFICIENCY,
          },

          {
            id: AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
        ].map((item) => setIsMetricDisplayedByDefault(item, isTicketingStrategy)),
      },
      {
        /* Conversation stages. */
        id: ReportElementIdsEnum.WORKLOAD_CONVERSATION_STAGES,
        metrics: [
          // TODO this will be removed once stage rules have been migrated to ticketing.
          {id: AnalyticsTableMetricsEnum.STAGE_PCT_RESOLVED_SEGMENTS_TABLE, isDisplayedByDefault: true},
        ],
      },
      {
        /* Workload by inbox. */
        id: ReportElementIdsEnum.WORKLOAD_BY_INBOX,
        metrics: [
          {
            id: AnalyticsTableMetricsEnum.WORKLOAD_INBOX_TABLE_WITH_TICKETING,
            isDisplayedByDefault: true,
          },
        ],
        columns: [
          {
            id: AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.MESSAGES_SENT,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS,
            category: MetricCategoriesEnum.VOLUME,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
          {
            id: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
            category: MetricCategoriesEnum.EFFICIENCY,
          },
        ].map((item) => setIsMetricDisplayedByDefault(item, isTicketingStrategy)),
      },
      // Only compute 'stage_num_active_segments' if we're using ticketing strategy.
      ...((isTicketingStrategy && [
        {
          id: ReportElementIdsEnum.WORKLOAD_STAGE_BANNER,
          metrics: [{id: AnalyticsScalarMetricsEnum.STAGE_NUM_ACTIVE_SEGMENTS, isDisplayedByDefault: true}],
        },
      ]) ||
        []),
    ],
    options: {
      [ReportFilterOptionsEnum.BUCKET_BOUNDARIES]: {
        // Default buckets for the ticket time histogram metric: < 1d, 1d - 2d, 2d - 4d, 4d - 7d, > 7d
        [AnalyticsTableMetricsEnum.TICKET_AVG_RESOLUTION_TIME_HISTO]: [
          86400000, 172800000, 345600000, 604800000,
        ],
      },
      [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [
        AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
        AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
        AnalyticsScalarMetricsEnum.MESSAGES_SENT,
        AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
        AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
        AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
        AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS,
        AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
        AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME,
        AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION,
        AnalyticsScalarMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY,
        AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH,
        AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH,
        AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH,
        AnalyticsGraphMetricsEnum.NUM_ACTIVE_TEAMMATES_GRAPH,
        AnalyticsGraphMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS_GRAPH,
        AnalyticsGraphMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH,
        AnalyticsGraphMetricsEnum.TICKET_AVG_RESOLUTION_TIME_GRAPH,
        AnalyticsGraphMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH,
        AnalyticsGraphMetricsEnum.RESPONSE_GRAPH,
        AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH,
        AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH,
        AnalyticsTableMetricsEnum.TICKET_AVG_RESOLUTION_TIME_HISTO,
        AnalyticsTableMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_HISTO,
        AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
        AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM,
      ],
    },
    featureStatus: AnalyticsFeatureStatusesEnum.NEW,
  };
}
